import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { computed, type Ref } from 'vue'
import { useHead } from '@unhead/vue'
import type { Script } from '@unhead/schema'
import { useRequestURL } from '#app'
import { isProfileInvitationPath } from '~/composables/profile-invitation'

export const UseAnalyticsScriptOrganizationFragment = graphql(`
    fragment UseAnalyticsScriptOrganizationFragment on Organization {
        googleAnalyticsTag
        dataLayerEcommerceEvents
        globalMarketingCode
        metaPixel
    }
`)

export function provideAnalyticsOrganizationFragment(
    organizationFragment: Ref<FragmentType<
        typeof UseAnalyticsScriptOrganizationFragment
    > | null> | null
) {
    // Omit the tags during SSR as well as hydration; analytics can read credentials before acceptance.
    // The navigation guard reloads when crossing the invitation boundary, keeping this document private.
    if (
        organizationFragment === null ||
        isProfileInvitationPath(useRequestURL().pathname)
    ) {
        return
    }

    const organizationData = computed(() => {
        if (organizationFragment.value === null) {
            return undefined
        }

        return getFragmentData(
            UseAnalyticsScriptOrganizationFragment,
            organizationFragment.value
        )
    })

    useHead({
        script: () => {
            if (organizationData.value === undefined) {
                return []
            }

            const tags: Script[] = []

            if (organizationData.value.googleAnalyticsTag !== '') {
                tags.push({
                    src: `https://www.googletagmanager.com/gtag/js?id=${organizationData.value.googleAnalyticsTag}`,
                    async: true
                })

                tags.push({
                    innerHTML: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${organizationData.value.googleAnalyticsTag}');`
                })
            }

            if (organizationData.value.dataLayerEcommerceEvents) {
                tags.push({
                    innerHTML: `window.dataLayer = window.dataLayer || [];`
                })
            }

            if (organizationData.value.globalMarketingCode !== '') {
                tags.push({
                    innerHTML: organizationData.value.globalMarketingCode
                })
            }

            if (organizationData.value.metaPixel !== '') {
                tags.push({
                    innerHTML: `!function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');fbq('init', '${organizationData.value.metaPixel}');fbq('track', 'PageView');`
                })
            }

            return tags
        }
    })
}
